import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal, SectionHeader, Corners } from "./Bits";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Access = () => {
  const [email, setEmail] = useState("");
  const [phase, setPhase] = useState("idle");
  const [operative, setOperative] = useState(null);
  const [count, setCount] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/access/count`)
      .then((r) => setCount(r.data.operatives))
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || phase === "sending") return;
    setPhase("sending");
    try {
      const { data } = await axios.post(`${API}/access`, { email });
      setOperative(data);
      setPhase("done");
      if (data.status === "ACCESS_GRANTED") {
        setCount((c) => (c !== null ? c + 1 : c));
        toast.success("UPLINK CONFIRMED // INTEL DISPATCHED");
      } else {
        toast.success("IDENTITY CONFIRMED // CLEARANCE RECOVERED");
      }
    } catch (err) {
      setPhase("idle");
      toast.error("TRANSMISSION FAILED // CHECK CHANNEL");
    }
  };

  return (
    <section
      id="access"
      data-testid="access-section"
      className="border-t border-line bg-void px-6 py-28 md:px-12 md:py-36"
    >
      <div className="mx-auto max-w-[1400px]">
        <SectionHeader
          index="04"
          label="ACCESS"
          title="Request Clearance"
          desc="Channel is encrypted. Your address is the only thing stored, and the only thing that will ever be asked of you."
        />
        <Reveal>
          <div className="clip-angle relative max-w-3xl border border-line bg-[#0D1117] p-8 md:p-12">
            <Corners />
            <div className="flex items-center gap-3 font-mono text-[10px] tracking-[0.3em] text-slate-500">
              <span className="h-2 w-2 animate-pulse bg-signal" />
              SECURE_UPLINK // TLS-ENABLED // NO LOGS KEPT
            </div>
            <p className="terminal-caret mt-8 font-mono text-sm text-slate-300">
              &gt; INITIATE CONTACT PROTOCOL
            </p>

            {phase === "done" && operative ? (
              <div
                data-testid="access-confirmation"
                className="mt-8 border border-signal/40 bg-signal/5 p-6 font-mono"
              >
                <p className="text-lg font-bold tracking-[0.2em] text-signal">
                  {operative.status === "ALREADY_ON_RECORD"
                    ? "IDENTITY CONFIRMED — WELCOME BACK, OPERATIVE"
                    : "ACCESS GRANTED — INTEL DISPATCHED"}
                </p>
                <p className="mt-3 text-xs tracking-widest text-slate-400">
                  OPERATIVE ID: <span className="text-[#F8FAFC]">{operative.operative_id}</span> //
                  STATUS: {operative.status}
                </p>
                <p className="terminal-caret mt-5 text-xs tracking-widest text-slate-500">
                  {operative.status === "ALREADY_ON_RECORD"
                    ? "YOUR CLEARANCE WAS RECOVERED FROM THE ARCHIVE"
                    : "STAND BY FOR DEPLOYMENT ORDERS"}
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-8 flex flex-col gap-6 sm:flex-row sm:items-end">
                <label className="flex-1">
                  <span className="font-mono text-[10px] tracking-[0.3em] text-slate-500">
                    &gt; ENTER COMMS CHANNEL (EMAIL)
                  </span>
                  <input
                    data-testid="access-email-input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="operative@proton.me"
                    className="mt-3 w-full border-b border-line bg-transparent py-3 font-mono text-sm text-[#F8FAFC] outline-none transition-colors placeholder:text-slate-700 focus:border-signal"
                  />
                </label>
                <button
                  data-testid="access-submit-btn"
                  type="submit"
                  disabled={phase === "sending"}
                  className="clip-angle-sm border border-signal px-8 py-3 font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-signal transition-colors hover:bg-signal hover:text-[#0A0E12] disabled:opacity-40"
                >
                  {phase === "sending" ? "TRANSMITTING..." : "TRANSMIT REQUEST"}
                </button>
              </form>
            )}

            <p
              data-testid="operative-count"
              className="mt-10 border-t border-line pt-5 font-mono text-[10px] tracking-[0.3em] text-slate-600"
            >
              OPERATIVES ON RECORD: <span className="text-signal">{count ?? "—"}</span>
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
